import { useEffect, useMemo, useState } from "react";
import { Plus, Download, Search, Trash2, RotateCcw, Archive } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, fmtDate, exportToCsv } from "@/lib/format";
import { StatusBadge, RatingBadge } from "@/components/StatusBadge";
import { RecordDialog } from "@/components/RecordDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

function followupBadges(row) {
  const out = [];
  const et = row.source_event_type || "";
  const t = (row.effective_followup_type || row.followup_type || "").toLowerCase();
  if (row.no_outbound) out.push(["No Outbound Contact", "bg-rose-100 text-rose-800 ring-rose-200"]);
  if (row.outreach_suppressed && !row.no_outbound) out.push(["Suppressed", "bg-rose-50 text-rose-700 ring-rose-200"]);
  if (row.licensed_agent_action_required) out.push(["Licensed Agent Required", "bg-amber-100 text-amber-900 ring-amber-300"]);
  if (et === "retention_risk" || t.includes("retention")) out.push(["Retention Risk", "bg-orange-100 text-orange-800 ring-orange-200"]);
  if (et === "nsf" || et === "returned_eft" || t.includes("nsf")) out.push(["NSF", "bg-red-100 text-red-800 ring-red-200"]);
  if (et === "first_reminder") out.push(["First Reminder", "bg-yellow-100 text-yellow-800 ring-yellow-200"]);
  if (row.carrier_verified) out.push(["Carrier Verified", "bg-emerald-100 text-emerald-800 ring-emerald-200"]);
  if (row.needs_human || row.automation_state === "human") out.push(["Needs Human", "bg-slate-200 text-slate-800 ring-slate-300"]);
  if (row.ai_generated) out.push(["AI Generated", "bg-indigo-100 text-indigo-800 ring-indigo-200"]);
  return out;
}

function renderCell(col, row) {
  const v = row[col.key];
  if (col.type === "currency") return fmtCurrency(v);
  if (col.type === "date") return fmtDate(v);
  if (col.type === "badge") return <StatusBadge value={v} />;
  if (col.type === "rating") return <RatingBadge value={v} />;
  if (col.type === "ai_badges") {
    const b = followupBadges(row);
    if (!b.length) return <span className="text-muted-foreground">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {b.map(([label, cls]) => (
          <span key={label} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${cls}`} data-testid={`fu-badge-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            {label}
          </span>
        ))}
      </div>
    );
  }
  if (col.type === "mono") return v ? <span className="font-mono text-xs">{v}</span> : <span className="text-muted-foreground">—</span>;
  if (col.type === "bool") return v ? "Yes" : "No";
  if ((v === "" || v == null) && col.emptyText) return <span className="text-muted-foreground italic">{col.emptyText}</span>;
  return v === "" || v == null ? <span className="text-muted-foreground">—</span> : v;
}

function RowActionCell({ row, rowAction }) {
  return (
    <TableCell onClick={(e) => e.stopPropagation()} className="whitespace-nowrap">
      <Button
        type="button"
        size="sm"
        variant={rowAction.variant || "outline"}
        className={rowAction.className || ""}
        onClick={() => rowAction.onClick?.(row)}
        data-testid={`${rowAction.testid || "row-action"}-${row.id}`}
      >
        {rowAction.label || "Open"}
      </Button>
    </TableCell>
  );
}

export function DataTable({
  columns, rows, onRowClick, canDelete, onDelete, restoreMode, rowAction,
  selectable = false, selectedIds = new Set(), onToggleRow, onToggleAll,
}) {
  const extraCols = (rowAction ? 1 : 0) + (canDelete ? 1 : 0) + (selectable ? 1 : 0);
  const actionAtStart = rowAction?.position === "start";
  const allSelected = selectable && rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someSelected = selectable && !allSelected && rows.some((r) => selectedIds.has(r.id));

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow className="bg-secondary/60 hover:bg-secondary/60">
            {selectable && (
              <TableHead className="w-10" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={(v) => onToggleAll?.(v === true)}
                  aria-label="Select all results"
                  data-testid="select-all-rows"
                />
              </TableHead>
            )}
            {rowAction && actionAtStart && <TableHead className="w-32" />}
            {columns.map((c) => (
              <TableHead
                key={c.key}
                className={`overline whitespace-nowrap ${c.align === "right" ? "text-right" : ""}`}
              >
                {c.label}
              </TableHead>
            ))}
            {rowAction && !actionAtStart && <TableHead className="w-32" />}
            {canDelete && <TableHead className="w-10" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length + extraCols} className="h-24 text-center text-muted-foreground">
                No records found.
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow
              key={row.id}
              className="cursor-pointer text-[13px]"
              onClick={() => onRowClick?.(row)}
              data-testid={`row-${row.id}`}
            >
              {selectable && (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.has(row.id)}
                    onCheckedChange={(v) => onToggleRow?.(row.id, v === true)}
                    aria-label={`Select ${row.display_id || row.id}`}
                    data-testid={`select-row-${row.id}`}
                  />
                </TableCell>
              )}
              {rowAction && actionAtStart && <RowActionCell row={row} rowAction={rowAction} />}
              {columns.map((c) => (
                <TableCell key={c.key} className={`whitespace-nowrap ${c.align === "right" ? "text-right tabular-nums" : ""}`}>
                  {renderCell(c, row)}
                </TableCell>
              ))}
              {rowAction && !actionAtStart && <RowActionCell row={row} rowAction={rowAction} />}
              {canDelete && (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <button
                    className={`rounded p-1 text-muted-foreground ${restoreMode ? "hover:text-emerald-600" : "hover:text-destructive"}`}
                    onClick={() => onDelete(row)}
                    data-testid={`${restoreMode ? "restore" : "delete"}-${row.id}`}
                    title={restoreMode ? "Restore" : "Delete"}
                  >
                    {restoreMode ? <RotateCcw className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function CrudModule({ module, subtitle, quickFilters, headerExtra, rowAction, selectionAction, refreshKey = 0 }) {
  const { user } = useAuth();
  const { title, collection, columns, fields, idLabel } = module;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [activeQuick, setActiveQuick] = useState("all");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const WRITE_ROLES = {
    quotes: ["admin", "ops_manager", "agent"], requotes: ["admin", "ops_manager", "agent"],
    policies: ["admin", "ops_manager", "agent"], followups: ["admin", "ops_manager", "agent"],
    commissions: ["admin", "ops_manager"], scorecard: ["admin", "ops_manager"],
  };
  const canWrite = (WRITE_ROLES[collection] || ["admin"]).includes(user.role);
  const canDelete = ["admin", "ops_manager"].includes(user.role);

  const load = () => {
    setLoading(true);
    setSelectedIds(new Set());
    api.get(`/${collection}`, { params: showDeleted ? { deleted: true } : {} })
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [collection, showDeleted, refreshKey]);

  const filtered = useMemo(() => {
    let base = rows;
    if (quickFilters && activeQuick !== "all") {
      const qf = quickFilters.find((f) => f.key === activeQuick);
      if (qf) base = base.filter(qf.test);
    }
    if (!search) return base;
    const q = search.toLowerCase();
    return base.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [rows, search, quickFilters, activeQuick]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [search, activeQuick]);

  const nextActionRow = useMemo(() => {
    if (!rowAction) return null;
    const openRows = filtered.filter((r) => !["completed", "no_action_needed"].includes(String(r.status_state || "").toLowerCase()));
    return [...openRows].sort((a, b) => {
      const da = a.due_date || "9999-12-31";
      const db = b.due_date || "9999-12-31";
      if (da !== db) return da.localeCompare(db);
      const rank = { high: 0, medium: 1, low: 2 };
      return (rank[String(a.priority || "").toLowerCase()] ?? 3) - (rank[String(b.priority || "").toLowerCase()] ?? 3);
    })[0] || null;
  }, [filtered, rowAction]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditing(row); setDialogOpen(true); };

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/${collection}/${editing.id}`, form);
        toast.success("Record updated");
      } else {
        await api.post(`/${collection}`, form);
        toast.success("Record created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${idLabel.toLowerCase()}? It can be restored from the Deleted view.`)) return;
    try {
      await api.delete(`/${collection}/${row.id}`);
      toast.success("Record deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const restore = async (row) => {
    try {
      await api.post(`/${collection}/${row.id}/restore`);
      toast.success("Record restored");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleRow = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    setSelectedIds(checked ? new Set(filtered.map((r) => r.id)) : new Set());
  };

  const bulkSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const verb = showDeleted ? "Restore" : "Delete";
    const note = showDeleted
      ? `Restore ${ids.length} selected record(s)?`
      : `Delete ${ids.length} selected record(s)? They can be restored from the Deleted view.`;
    if (!window.confirm(note)) return;

    setBulkBusy(true);
    let succeeded = 0;
    let failed = 0;
    try {
      for (let i = 0; i < ids.length; i += 20) {
        const batch = ids.slice(i, i + 20);
        const results = await Promise.allSettled(batch.map((id) => (
          showDeleted ? api.post(`/${collection}/${id}/restore`) : api.delete(`/${collection}/${id}`)
        )));
        results.forEach((r) => {
          if (r.status === "fulfilled") succeeded += 1;
          else failed += 1;
        });
      }
      if (succeeded) toast.success(`${verb}d ${succeeded} record(s).`);
      if (failed) toast.error(`${failed} record(s) could not be ${showDeleted ? "restored" : "deleted"}.`);
      load();
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div data-testid={`${collection}-module`}>
      <PageHeader title={title} subtitle={subtitle}>
        {headerExtra}
        {rowAction?.headerLabel && canWrite && !showDeleted && (
          <Button
            type="button"
            onClick={() => nextActionRow && rowAction.onClick?.(nextActionRow)}
            disabled={!nextActionRow}
            className={rowAction.headerClassName || ""}
            data-testid={rowAction.headerTestid || "work-next"}
          >
            {rowAction.headerLabel}
          </Button>
        )}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="search-input"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-44 sm:w-56"
          />
        </div>
        <Button variant="outline" onClick={() => exportToCsv(`${collection}.csv`, filtered, columns)} data-testid="export-btn">
          <Download className="mr-1.5 h-4 w-4" /> Export
        </Button>
        {canDelete && (
          <Button variant={showDeleted ? "default" : "outline"} onClick={() => setShowDeleted((s) => !s)} data-testid="toggle-deleted-btn">
            <Archive className="mr-1.5 h-4 w-4" /> {showDeleted ? "Active" : "Deleted"}
          </Button>
        )}
        {canWrite && !showDeleted && (
          <Button onClick={openNew} data-testid="add-record-btn">
            <Plus className="mr-1.5 h-4 w-4" /> New {idLabel}
          </Button>
        )}
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{filtered.length} {showDeleted ? "deleted " : ""}record(s)</span>
        {(canDelete || selectionAction) && selectedIds.size > 0 && (
          <div className="flex items-center gap-2" data-testid="bulk-selection-bar">
            <span className="font-medium text-foreground">{selectedIds.size} selected</span>
            {selectionAction && !showDeleted && (
              <Button
                size="sm"
                variant={selectionAction.variant || "outline"}
                onClick={() => selectionAction.onClick?.([...selectedIds])}
                data-testid={selectionAction.testid || "selection-action-btn"}
              >
                {selectionAction.label}
              </Button>
            )}
            {canDelete && (
              <Button
                size="sm"
                variant={showDeleted ? "outline" : "destructive"}
                onClick={bulkSelected}
                disabled={bulkBusy}
                data-testid={showDeleted ? "restore-selected-btn" : "delete-selected-btn"}
              >
                {showDeleted ? <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> : <Trash2 className="mr-1.5 h-3.5 w-3.5" />}
                {bulkBusy ? "Working…" : `${showDeleted ? "Restore" : "Delete"} Selected`}
              </Button>
            )}
          </div>
        )}
      </div>

      {quickFilters && (
        <div className="mb-3 flex flex-wrap gap-2" data-testid="quick-filters">
          {[{ key: "all", label: `All (${rows.length})` }, ...quickFilters.map((f) => ({ ...f, label: `${f.label} (${rows.filter(f.test).length})` }))].map((f) => (
            <button key={f.key} onClick={() => setActiveQuick(f.key)} data-testid={`quickfilter-${f.key}`}
              className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${activeQuick === f.key ? "bg-[#17324D] text-white ring-[#17324D]" : "bg-card text-muted-foreground ring-border hover:text-foreground"}`}>
              {f.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center h-48 text-muted-foreground">Loading…</div>
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          onRowClick={canWrite && !showDeleted ? openEdit : undefined}
          canDelete={canDelete}
          onDelete={showDeleted ? restore : remove}
          restoreMode={showDeleted}
          rowAction={canWrite && !showDeleted ? rowAction : null}
          selectable={canDelete || Boolean(selectionAction)}
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
        />
      )}

      <RecordDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editing ? `Edit ${idLabel}` : `New ${idLabel}`}
        fields={fields}
        initial={editing}
        onSave={save}
        saving={saving}
        calc={module.calc}
        entityType={module.entity}
      />
    </div>
  );
}
